/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */
import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Grid,
  Tooltip,
  InputAdornment,
  Autocomplete,
  Alert,
  FormHelperText,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { assetsTypeList, solarMountingTypes, gensetTypes } from "../../lib/siteData";
import RenameModal from "../Common/RenameModal";
import { ReactComponent as DieselGeneratorIcon } from "../../static/images/enapter-diesel-generator-72.svg";
import { ReactComponent as EnergyStorageIcon } from "../../static/images/enapter-energy-storage-72.svg";
import { ReactComponent as MicrochipIcon } from "../../static/images/enapter-microchip-72.svg";
import { ReactComponent as SolarPanelIcon } from "../../static/images/enapter-solar-panel-72.svg";
import { ReactComponent as UtilityIcon } from "../../static/images/enapter-electrical-grid-72.svg";
import { useTheme } from "@mui/material/styles";
import { IconButton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "@tanstack/react-query";
import {
  putAssetMetadataMutation,
  getManufacturersListQuery,
  postManufacturerMutation,
} from "../../lib/Queries";
import ArrowCircleRightOutlined from "@mui/icons-material/ArrowCircleRightOutlined";
import { TimeSeriesMetric } from "../../lib/Types";

interface SourceInfo {
  source: string;
  id: number;
  sub_source: string;
}

interface SiteAssetOption {
  uuid: string;
  scaleos_name?: string | null;
  source_name?: string | null;
  name?: string | null;
}

interface Asset {
  id: string;
  name: string;
  scaleosName: string;
  scaleosType: string;
  outputCapacity: number;
  source: string;
  manufacturer: string;
  model: string;
  serialNumber: string;
  solarMounting: string;
  enableAlarms: boolean;
  showInScale: boolean;
  storageCapacity: number;
  gensetUse: string;
  runHoursLimit: number;
  sourceInfo: SourceInfo[];
  sources: string[];
  edgeId: string | null;
  parentAsset?: string | null;
  timeSeries?: TimeSeriesMetric[];
  lastUpdate?: string;
}

interface Props {
  asset: Asset;
  site: {
    uuid: string;
    name: string;
    group_id: string;
  };
  siteAssets?: SiteAssetOption[];
  preFill: boolean;
  isOpen: boolean;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onSave?: () => void;
  onSaveAsset?: (updatedAsset: Asset) => void;
  index: number;
  preFillDirty: boolean;
}

const assetDisplayName = (a: SiteAssetOption): string =>
  a.scaleos_name || a.source_name || a.name || "";

const withMetricTypes = (
  metrics: TimeSeriesMetric[],
  powerUuid: string | null,
  socUuid: string | null,
  isBess: boolean,
): TimeSeriesMetric[] =>
  metrics.map((m) => {
    let metric_type: string | null = m.metric_type ?? null;
    if (metric_type === "power" || metric_type === "soc") metric_type = null;
    if (powerUuid && m.uuid === powerUuid) metric_type = "power";
    // Power wins if both dropdowns somehow share a UUID.
    else if (isBess && socUuid && m.uuid === socUuid) metric_type = "soc";
    return { ...m, metric_type };
  });

export default function AssetSetupModal({
  asset,
  site,
  siteAssets = [],
  isOpen,
  preFill,
  onClose,
  onPrev,
  onNext,
  onSave,
  onSaveAsset,
  index,
  preFillDirty,
}: Props) {
  const theme = useTheme();
  const [scaleosName, setScaleosName] = useState<string | null>(asset.scaleosName ?? null);
  const [scaleosType, setScaleosType] = useState<string | null>(asset.scaleosType ?? null);
  const [outputCapacity, setOutputCapacity] = useState<number | null>(asset.outputCapacity ?? null);
  const [source, setSource] = useState<string | null>(asset.source ?? null);
  const [manufacturer, setManufacturer] = useState<string | null>(asset.manufacturer ?? null);
  const [model, setModel] = useState<string | null>(asset.model ?? null);
  const [serialNumber, setSerialNumber] = useState<string | null>(asset.serialNumber ?? null);
  const [solarMounting, setSolarMounting] = useState<string | null>(asset.solarMounting ?? null);
  const [enableAlarms, setEnableAlarms] = useState<boolean>(asset.enableAlarms ?? true);
  const [showInScale, setShowInScale] = useState<boolean>(
    asset.scaleosType === "Controller" ? false : (asset.showInScale ?? true),
  );
  const [storageCapacity, setStorageCapacity] = useState<number | null>(
    asset.storageCapacity ?? null,
  );
  const [gensetUse, setGensetUse] = useState<string>(asset.gensetUse ?? null);
  const [runHoursLimit, setRunHoursLimit] = useState<string>(asset.runHoursLimit ?? null);
  const [parentAsset, setParentAsset] = useState<string | null>(asset.parentAsset ?? null);
  const [metrics, setMetrics] = useState<TimeSeriesMetric[]>(asset.timeSeries ?? []);
  const [powerMetric, setPowerMetric] = useState<string | null>(
    asset.timeSeries?.find((m) => m.metric_type === "power")?.uuid ?? null,
  );
  const [socMetric, setSocMetric] = useState<string | null>(
    asset.timeSeries?.find((m) => m.metric_type === "soc")?.uuid ?? null,
  );
  const [dirty, setDirty] = useState(preFillDirty ?? false);
  const [typedManufacturer, setTypedManufacturer] = useState("");
  const [typedModel, setTypedModel] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [inputModelValue, setInputModelValue] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [openModelModal, setOpenModelModal] = useState(false);

  const parentAssetOptions = useMemo(
    () =>
      [...siteAssets]
        .filter((a) => a.uuid && a.uuid !== asset.id)
        .sort((a, b) => assetDisplayName(a).localeCompare(assetDisplayName(b))),
    [siteAssets, asset.id],
  );

  useEffect(() => {
    setScaleosName(asset.scaleosName ?? null);
    setScaleosType(asset.scaleosType ?? null);
    setOutputCapacity(asset.outputCapacity ?? null);
    setSource(asset.source ?? null);
    setManufacturer(asset.manufacturer ?? null);
    setModel(asset.model ?? null);
    setSerialNumber(asset.serialNumber ?? null);
    setSolarMounting(asset.solarMounting ?? null);
    setEnableAlarms(asset.enableAlarms ?? true);
    setShowInScale(asset.scaleosType === "Controller" ? false : (asset.showInScale ?? true));
    setStorageCapacity(asset.storageCapacity ?? null);
    setGensetUse(asset.gensetUse ?? null);
    setRunHoursLimit(asset.runHoursLimit ?? null);
    setParentAsset(asset.parentAsset ?? null);
    const nextMetrics = asset.timeSeries ?? [];
    setMetrics(nextMetrics);
    setPowerMetric(nextMetrics.find((m) => m.metric_type === "power")?.uuid ?? null);
    setSocMetric(nextMetrics.find((m) => m.metric_type === "soc")?.uuid ?? null);
    setTypedManufacturer("");
    setTypedModel("");
  }, [index, isOpen, onPrev, onNext]);
  const mutation = useMutation({
    ...putAssetMetadataMutation(),
    onSuccess: () => {},
  });

  const mutationNewManufacturer = useMutation({
    ...postManufacturerMutation(),
  });
  const getAssetIcon = (type: string | null) => {
    switch (type) {
      case "Solar":
        return <SolarPanelIcon />;
      case "Inverter":
        return <SolarPanelIcon />;
      case "BESS":
        return <EnergyStorageIcon />;
      case "Genset":
        return <DieselGeneratorIcon />;
      case "Controller":
        return <MicrochipIcon />;
      case "Utility":
        return <UtilityIcon />;
    }
  };

  const allFieldsFilled = () => {
    if (!scaleosType) {
      return false;
    }

    if (scaleosType === "BESS") {
      return outputCapacity && storageCapacity;
    }

    if (scaleosType === "Controller" || scaleosType === "Utility") {
      return true;
    }

    return !!outputCapacity;
  };

  const isController = scaleosType === "Controller";

  const saveSettings = () => {
    const isBess = scaleosType === "BESS";
    const updatedAsset = {
      uuid: asset.id,
      name: asset.name,
      scaleos_name: scaleosName,
      scaleos_type: scaleosType,
      power_capacity: outputCapacity,
      energy_capacity: storageCapacity,
      trigger_alarms: enableAlarms,
      trigger_data_alarms: enableAlarms,
      manufacturer: manufacturer,
      model: model,
      serial_number: serialNumber,
      mounting: solarMounting,
      weight: isController ? false : showInScale,
      source_info: asset.sourceInfo,
      siteName: site.name,
      siteUuid: site.uuid,
      siteGroupId: site.group_id ?? null,
      genset_use: gensetUse,
      run_hours_limit: runHoursLimit,
      source: source,
      sources: asset.sources,
      group_id: asset.edgeId,
      parent_asset: parentAsset || null,
      time_series: withMetricTypes(
        metrics,
        isController ? null : powerMetric,
        isBess ? socMetric : null,
        isBess,
      ),
    };
    mutation.mutate(updatedAsset, {
      onSuccess: () => {
        if (preFill && onSaveAsset) {
          onSaveAsset(updatedAsset);
        } else {
          onSave();
          onClose();
        }
        setDirty(false);
      },
    });
  };

  const {
    data: manufacturersList,
    isLoading: manufacturersListIsLoading,
    refetch: refetchManufacturersList,
  } = useQuery({
    ...getManufacturersListQuery(scaleosType),
    select: (data) =>
      data
        ?.map((manufacturer) => ({
          ...manufacturer,
          model: manufacturer.model?.sort((a, b) => a.name.localeCompare(b.name)), // Sort models alphabetically
        }))
        .sort((a, b) => a.name.localeCompare(b.name)), // Sort manufacturers alphabetically
  });

  const handleSaveManufacturer = (modalName) => {
    const manufacturerData = {
      name: modalName,
    };
    mutationNewManufacturer.mutate(manufacturerData, {
      onSuccess: () => {
        refetchManufacturersList();
        setOpenModal(false);
        setTypedManufacturer(modalName);
        setDirty(true);
      },
    });
  };

  const handleSaveModel = (modalName) => {
    const manufacturerModelData = {
      uuid: manufacturer,
      model: [
        {
          name: modalName,
          scaleos_type: scaleosType,
          output_capacity: outputCapacity,
        },
      ],
    };
    mutationNewManufacturer.mutate(manufacturerModelData, {
      onSuccess: () => {
        refetchManufacturersList();
        setOpenModelModal(false);
        setTypedModel(modalName);
        setDirty(true);
      },
    });
  };

  useEffect(() => {
    if (scaleosType) {
      refetchManufacturersList();
    }
  }, [scaleosType]);

  useEffect(() => {
    if (typedManufacturer && Array.isArray(manufacturersList)) {
      const matchedManufacturer = manufacturersList.find((m) => m.name === typedManufacturer);
      if (matchedManufacturer) {
        setManufacturer(matchedManufacturer.uuid);
      }
    }
  }, [manufacturersList, typedManufacturer]);

  useEffect(() => {
    if (typedModel && Array.isArray(manufacturersList)) {
      const selectedManufacturer = manufacturersList.find((m) => m.uuid === manufacturer);
      if (selectedManufacturer && Array.isArray(selectedManufacturer.model)) {
        const matchedModel = selectedManufacturer.model.find((m) => m.name === typedModel);
        if (matchedModel) {
          setModel(matchedModel.uuid);
        }
      }
    }
  }, [manufacturersList, typedModel]);

  const metricSelect = (
    label: string,
    value: string | null,
    setValue: (v: string | null) => void,
    other: string | null,
    setOther: (v: string | null) => void,
    labelId = `${label.toLowerCase().replace(/ /g, "-")}-label`,
  ) => (
    <Grid item xs={3}>
      <FormControl fullWidth>
        <InputLabel size="small" id={labelId}>
          {label}
        </InputLabel>
        <Select
          size="small"
          labelId={labelId}
          label={label}
          value={value ?? ""}
          onChange={(e) => {
            const next = e.target.value || null;
            setValue(next);
            if (next && next === other) setOther(null);
            setDirty(true);
          }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {metrics.map((metric) => (
            <MenuItem key={metric.uuid} value={metric.uuid} disabled={metric.uuid === other}>
              {metric.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 848,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: 32,
            right: 32,
            pointerEvents: "none",
          }}
          aria-label="asset type"
          disableRipple
        >
          {getAssetIcon(scaleosType)}
        </IconButton>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Asset Setup
        </Typography>

        <Typography sx={{ mb: 2 }} variant="h5">
          {scaleosName || asset.name}
        </Typography>

        <Typography sx={{ mb: 3, color: "text.secondary" }} variant="subtitle2">
          {asset.name}
          {"\u00A0\u00A0"}•{"\u00A0\u00A0"}Available since {asset.lastUpdate}
        </Typography>

        <Grid container spacing={scaleosType === "Utility" ? 1 : 2}>
          {/* Row 1: Scale Name, Asset Type, Output Capacity, Source */}
          <Grid item xs={3}>
            <FormControl fullWidth>
              <TextField
                label="Scale Name"
                size="small"
                disabled={asset.sources?.length > 1}
                value={scaleosName ?? ""}
                onChange={(e) => {
                  setScaleosName(e.target.value);
                  setDirty(true);
                }}
                onBlur={(ev) => {
                  setScaleosName(ev.target.value.trim()); // Clean up input when user finishes
                }}
                helperText={
                  <Tooltip
                    title={
                      <>
                        Scale Name appears in Overview, Specs, Markets, and the customer portal.
                        <br />
                        <br />
                        If there is no Scale Name, the Asset Name is displayed.
                      </>
                    }
                    arrow
                  >
                    <span>Where does this appear?</span>
                  </Tooltip>
                }
                FormHelperTextProps={{
                  sx: { color: "primary.main", textDecoration: "underline" },
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <InputLabel size="small" required>
                Asset Type
              </InputLabel>
              <Select
                size="small"
                value={scaleosType}
                label="Asset Type"
                onChange={(e) => {
                  setScaleosType(e.target.value);
                  setDirty(true);
                }}
              >
                {assetsTypeList.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {["Inverter", "Controller"].includes(scaleosType) && (
                <FormHelperText>{scaleosType} data is only shown in Grapher.</FormHelperText>
              )}
            </FormControl>
          </Grid>
          {!["Controller", "Utility"].includes(scaleosType) && (
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Output Capacity"
                required
                type="number"
                size="small"
                value={outputCapacity}
                onChange={(e) => {
                  const value = e.target.value;
                  setOutputCapacity(value ? Number(value) : null);
                  setDirty(true);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kW</InputAdornment>,
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
          <Grid item xs={3}>
            <FormControl fullWidth>
              <InputLabel size="small" required>
                Source
              </InputLabel>
              <Select
                size="small"
                label="Source"
                disabled={asset.sources?.length < 2}
                value={source}
                onChange={(e) => {
                  setSource(e.target.value);
                  setDirty(true);
                }}
              >
                {asset.sources?.map((src, index) => (
                  <MenuItem key={index} value={src}>
                    {src}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{ mb: scaleosType === "Utility" ? 0 : undefined }} />
          {/* Row 2: Parent Asset (+ Storage Capacity for BESS / Solar Mounting for PV) */}
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel size="small" id="parent-asset-label">
                Parent Asset
              </InputLabel>
              <Select
                size="small"
                labelId="parent-asset-label"
                label="Parent Asset"
                value={parentAsset ?? ""}
                onChange={(e) => {
                  setParentAsset(e.target.value || null);
                  setDirty(true);
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {parentAssetOptions.map((option) => (
                  <MenuItem key={option.uuid} value={option.uuid}>
                    {assetDisplayName(option)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Assets are grouped under their parents in Site Overview.
              </FormHelperText>
            </FormControl>
          </Grid>
          {scaleosType === "BESS" && (
            <Grid item xs={3}>
              <TextField
                fullWidth
                label="Storage Capacity"
                size="small"
                type="number"
                required
                value={storageCapacity}
                onChange={(e) => {
                  const value = e.target.value;
                  setStorageCapacity(value ? Number(value) : null);
                  setDirty(true);
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
          {["Solar", "Inverter"].includes(scaleosType) && (
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel size="small">Solar Mounting</InputLabel>
                <Select
                  size="small"
                  value={solarMounting}
                  label="Solar Mounting"
                  onChange={(e) => {
                    setSolarMounting(e.target.value);
                    setDirty(true);
                  }}
                >
                  {solarMountingTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} />
          {/* Row 3: Power Metric (+ SOC Metric for BESS) */}
          {!isController &&
            metricSelect(
              "Power Metric",
              powerMetric,
              setPowerMetric,
              scaleosType === "BESS" ? socMetric : null,
              setSocMetric,
            )}
          {scaleosType === "BESS" &&
            metricSelect("SOC Metric", socMetric, setSocMetric, powerMetric, setPowerMetric)}
          <Grid item xs={12} />
          {/* Row 4: Manufacturer, Model, Serial No */}
          {"Utility" !== scaleosType && (
            <>
              <Grid item xs={3}>
                <Autocomplete
                  id="manufacturer"
                  size="small"
                  noOptionsText={
                    <MenuItem
                      style={{ cursor: "pointer", color: "black" }}
                      onClick={() => {
                        setTypedManufacturer(inputValue);
                        setOpenModal(true);
                      }}
                    >
                      {`Add "${inputValue}"`}
                    </MenuItem>
                  }
                  disabled={!scaleosType}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
                  options={manufacturersList || []}
                  loading={manufacturersListIsLoading}
                  value={manufacturersList?.find((item: any) => item.uuid === manufacturer) || null}
                  onChange={(event, newValue) => {
                    setManufacturer(newValue ? newValue.uuid : "");
                    setModel(null);
                    setDirty(true);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Manufacturer" variant="outlined" />
                  )}
                  getOptionLabel={(option) => option.name}
                  key={(option) => option.uuid}
                />
                {/* Modal for adding a new option */}
                <RenameModal
                  isOpen={openModal}
                  onClose={() => setOpenModal(false)}
                  onSave={(name) => handleSaveManufacturer(name)}
                  currentName={typedManufacturer}
                  title="Add a Manufacturer"
                  label="Manufacturer Name"
                  okButtonLabel="Create Manufacturer"
                  cancelButtonLabel="Cancel"
                />
              </Grid>

              <Grid item xs={3}>
                <Autocomplete
                  id="model"
                  size="small"
                  disabled={!manufacturer}
                  noOptionsText={
                    <MenuItem
                      style={{ cursor: "pointer", color: "black" }}
                      onClick={() => {
                        setTypedModel(inputModelValue);
                        setOpenModelModal(true);
                      }}
                    >
                      {`Add "${inputModelValue}"`}
                    </MenuItem>
                  }
                  inputValue={inputModelValue}
                  onInputChange={(event, newInputValue) => setInputModelValue(newInputValue)}
                  options={
                    manufacturersList
                      ?.find((m) => m.uuid === manufacturer) // Find the selected manufacturer
                      ?.model?.map((m) => ({
                        uuid: m.uuid,
                        name: m.name,
                      })) || [] // If no models, return an empty array
                  }
                  loading={manufacturersListIsLoading}
                  value={
                    manufacturer
                      ? manufacturersList
                          ?.flatMap((manufacturer) => manufacturer.model || [])
                          .find((m) => m.uuid === model) || null
                      : null
                  }
                  onChange={(event, newValue) => {
                    setModel(newValue ? newValue.uuid : "");
                    setDirty(true);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Model" variant="outlined" />
                  )}
                  getOptionLabel={(option) => option?.name || ""}
                  key={(option) => option.uuid}
                />

                <RenameModal
                  isOpen={openModelModal}
                  onClose={() => setOpenModelModal(false)}
                  onSave={(name) => handleSaveModel(name)}
                  currentName={typedModel}
                  title="Add a Model"
                  label="Model Name"
                  okButtonLabel="Create Model"
                  cancelButtonLabel="Cancel"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Serial No"
                  size="small"
                  value={serialNumber}
                  onChange={(e) => {
                    setSerialNumber(e.target.value);
                    setDirty(true);
                  }}
                  onBlur={(ev) => {
                    setSerialNumber(ev.target.value.trim()); // Clean up input when user finishes
                  }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}></Grid>
          {/* Row 5: Genset Type */}
          {scaleosType === "Genset" && (
            <>
              <Grid item xs={3}>
                <FormControl fullWidth>
                  <InputLabel size="small">Genset Use</InputLabel>
                  <Select
                    size="small"
                    value={gensetUse}
                    label="Genset Use"
                    onChange={(e) => {
                      setGensetUse(e.target.value);
                      setDirty(true);
                    }}
                  >
                    {gensetTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Run Hours Limit"
                  size="small"
                  type="number"
                  value={runHoursLimit}
                  onChange={(e) => {
                    const value = e.target.value;
                    setRunHoursLimit(value ? Number(value) : null);
                    setDirty(true);
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">h</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
          {/* Row 4: Checkboxes */}
          <Grid item xs={12} />
          {scaleosType !== "Controller" && (
            <Grid sx={{ ml: 1 }} item xs={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enableAlarms}
                    onChange={(e) => {
                      setEnableAlarms(e.target.checked);
                      setDirty(true);
                    }}
                    sx={{ transform: "scale(1.5)" }}
                  />
                }
                label={<Typography variant="body1">Enable alarms</Typography>}
              />
            </Grid>
          )}
          {!isController && (
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showInScale}
                    onChange={(e) => {
                      setShowInScale(e.target.checked);
                      setDirty(true);
                    }}
                    sx={{ transform: "scale(1.5)" }}
                  />
                }
                label={<Typography variant="body1">Show in Scale software</Typography>}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            {preFill && (
              <Alert
                icon={<ArrowCircleRightOutlined fontSize="small" />}
                severity="info"
                sx={{
                  backgroundColor: "#E5F6FD",
                  color: "#014361",
                  mb: 2,
                }}
              >
                For faster setup, most asset info will pre-fill the next asset in the setup flow.
              </Alert>
            )}
          </Grid>
          {/* Row 5: Buttons */}
          <Grid item xs={12} sx={{ display: "flex" }}>
            {preFill && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  disabled={!onPrev}
                  onClick={onPrev}
                  sx={{
                    color: "black",
                    borderColor: "rgba(0, 0, 0, 0.87)",
                  }}
                  variant="outlined"
                >
                  View Previous
                </Button>
                <Button
                  disabled={!onNext}
                  onClick={onNext}
                  sx={{
                    color: "black",
                    borderColor: "rgba(0, 0, 0, 0.87)",
                  }}
                  variant="outlined"
                >
                  View Next
                </Button>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 2, ml: "auto" }}>
              <Button
                onClick={onClose}
                sx={{
                  color: "black",
                  borderColor: "rgba(0, 0, 0, 0.87)",
                }}
                variant="outlined"
              >
                Close
              </Button>
              <LoadingButton
                loading={mutation?.isLoading}
                onClick={allFieldsFilled() && dirty ? saveSettings : () => {}}
                variant="contained"
                disabled={(!allFieldsFilled() && !mutation?.isSuccess) || !dirty}
              >
                {preFill && onNext ? "Save & Pre-fill Next" : "Save Changes"}
              </LoadingButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}
